import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Monitor, Circle, AlertCircle, Loader2, User } from 'lucide-react';
import { machinesApi, MACHINE_STATUS } from '../../../../utils/api/machines';

/**
 * StatusIndicator - Shows online/offline status dot
 * Defined outside MachineSelector to prevent re-creation on each render
 */
const StatusIndicator = ({ status }) => {
  const colors = {
    [MACHINE_STATUS.ONLINE]: 'text-green-400',
    [MACHINE_STATUS.OFFLINE]: 'text-gray-500',
    [MACHINE_STATUS.ERROR]: 'text-red-400'
  };
  return (
    <Circle 
      size={8} 
      className={`${colors[status] || colors[MACHINE_STATUS.OFFLINE]} fill-current`} 
    />
  );
};

/**
 * MachineRow - Single machine option in dropdown
 * Defined outside MachineSelector to prevent re-creation on each render
 */
const MachineRow = ({ machine, isSelected, disabled, onSelect }) => (
  <button
    type="button"
    onClick={() => {
      if (!disabled && onSelect) {
        onSelect(machine);
      }
    }}
    disabled={disabled}
    className={`
      w-full px-4 py-3 flex items-center gap-3 text-left transition-colors
      ${isSelected ? 'bg-blue-500/20 border-l-2 border-blue-500' : 'border-l-2 border-transparent'}
      ${disabled 
        ? 'opacity-50 cursor-not-allowed' 
        : 'hover:bg-gray-700/50 cursor-pointer'
      }
    `}
  >
    <StatusIndicator status={machine.status} />
    <Monitor size={16} className="text-gray-400" />
    <div className="flex-1 min-w-0">
      <div className="font-medium text-white truncate">{machine.hostname}</div>
      <div className="text-xs text-gray-500 flex items-center gap-2">
        <span>{machine.os_type}</span>
        {machine.current_user && (
          <>
            <span>•</span>
            <span className="flex items-center gap-1">
              <User size={10} />
              {machine.current_user}
            </span>
          </>
        )}
      </div>
    </div>
    {machine.capabilities && (
      <span className="text-xs text-gray-500">
        {machine.capabilities.length} apps
      </span>
    )}
  </button>
);

/**
 * MachineSelector - Dropdown for selecting target workstation
 * 
 * Props:
 * - selectedMachine: currently selected machine object
 * - onSelect: callback when machine is selected (machine) => void
 * - className: additional CSS classes
 */
const MachineSelector = ({ selectedMachine, onSelect, className = '' }) => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);

  // Fetch machines on mount
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await machinesApi.list({ includeCapabilities: true });
        
        // Machines are already sorted by the API helper
        setMachines(data.machines || []);
        
        // Auto-select first online machine if none selected
        if (!selectedMachine && data.machines?.length > 0) {
          const firstOnline = data.machines.find(m => m.status === MACHINE_STATUS.ONLINE);
          if (firstOnline) {
            onSelect(firstOnline);
          }
        }
      } catch (err) {
        console.error('Failed to fetch machines:', err);
        setError('Failed to load machines');
      } finally {
        setLoading(false);
      }
    };

    fetchMachines();
  }, []);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle machine selection
  const handleSelectMachine = (machine) => {
    onSelect(machine);
    setIsOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-4 py-3 bg-gray-800/50 rounded-lg ${className}`}>
        <Loader2 size={16} className="animate-spin text-gray-400" />
        <span className="text-gray-400">Loading machines...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg ${className}`}>
        <AlertCircle size={16} className="text-red-400" />
        <span className="text-red-400">{error}</span>
        <button 
          onClick={() => window.location.reload()}
          className="ml-auto text-xs text-red-400 hover:text-red-300 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // No machines state
  if (machines.length === 0) {
    return (
      <div className={`flex items-center gap-2 px-4 py-3 bg-gray-800/50 rounded-lg ${className}`}>
        <Monitor size={16} className="text-gray-500" />
        <span className="text-gray-400">No machines registered</span>
      </div>
    );
  }

  const onlineMachines = machines.filter(m => m.status === MACHINE_STATUS.ONLINE);
  const offlineMachines = machines.filter(m => m.status !== MACHINE_STATUS.ONLINE);

  return (
    <div className={`relative ${className}`}>
      {/* Dropdown trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 
          bg-gray-800/50 hover:bg-gray-700/50 
          border border-gray-700 rounded-lg
          transition-colors cursor-pointer
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
        `}
      >
        {selectedMachine ? (
          <>
            <StatusIndicator status={selectedMachine.status} />
            <Monitor size={18} className="text-gray-400" />
            <div className="flex-1 text-left min-w-0">
              <div className="font-medium text-white truncate">
                {selectedMachine.hostname}
              </div>
              <div className="text-xs text-gray-500">
                {selectedMachine.os_type}
                {selectedMachine.current_user && ` • ${selectedMachine.current_user}`}
              </div>
            </div>
          </>
        ) : (
          <>
            <Monitor size={18} className="text-gray-400" />
            <span className="flex-1 text-left text-gray-400">Select a machine</span>
          </>
        )}
        <ChevronDown 
          size={18} 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown menu - rendered via portal to escape overflow:hidden containers */}
      {isOpen && createPortal(
        <>
          {/* Invisible backdrop to catch outside clicks */}
          <div 
            className="fixed inset-0" 
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu - positioned based on trigger button */}
          <div 
            className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
            style={{ 
              zIndex: 9999,
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
          >
            <div className="max-h-64 overflow-y-auto">
              {/* Online machines section */}
              {onlineMachines.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-900/50 sticky top-0">
                    Online ({onlineMachines.length})
                  </div>
                  {onlineMachines.map(machine => (
                    <MachineRow
                      key={machine.machine_id}
                      machine={machine}
                      isSelected={selectedMachine?.machine_id === machine.machine_id}
                      disabled={false}
                      onSelect={handleSelectMachine}
                    />
                  ))}
                </div>
              )}

              {/* Offline machines section */}
              {offlineMachines.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-900/50 sticky top-0">
                    Offline ({offlineMachines.length})
                  </div>
                  {offlineMachines.map(machine => (
                    <MachineRow
                      key={machine.machine_id}
                      machine={machine}
                      isSelected={false}
                      disabled={true}
                      onSelect={handleSelectMachine}
                    />
                  ))}
                </div>
              )}

              {/* All offline warning */}
              {onlineMachines.length === 0 && offlineMachines.length > 0 && (
                <div className="px-4 py-3 text-center text-amber-400 text-sm bg-amber-500/10">
                  <AlertCircle size={16} className="inline mr-2" />
                  No machines are currently online
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default MachineSelector;
